import { FC, useContext, useState, useEffect, useCallback } from 'react';
import { ALL_PERMISSIONS } from './registry';
import {
  Users, CheckCircle, XCircle, Shield, Trash2,
  RefreshCw, Clock, ChevronDown, Save, X, KeyRound, Eye, EyeOff,
} from 'lucide-react';
import ContextStore from '../hooks/ContextStore';
import { dispatch } from '../dispatch/dispatch';
import { actions } from '../dispatch';
import './AdminUsers.page.css';

// ─── Types ───────────────────────────────────────────────────────────────────
interface AdminUser {
  _id:         string;
  email:       string;
  type:        'admin' | 'user';
  approved:    boolean;
  denied:      boolean;
  permissions: string[];
  createdAt:   string;
}

// ─── Page keys: one checkbox per page, straight from src/pages/registry.ts ───
const ALL_PAGES = ALL_PERMISSIONS;

const fmtDate = (s: string) => {
  if (!s) return '—';
  return new Date(s).toLocaleDateString('en-GB', { day:'2-digit', month:'short', year:'numeric' });
};

// ─── Permission Editor ────────────────────────────────────────────────────────
const PermEditor: FC<{
  user: AdminUser;
  onSave: (perms: string[]) => void;
  onClose: () => void;
}> = ({ user, onSave, onClose }) => {
  const [perms, setPerms] = useState<string[]>(user.permissions ?? []);
  const isAll = perms.includes('all');

  const toggle = (key: string) => {
    if (key === 'all') {
      setPerms(isAll ? [] : ['all']);
      return;
    }
    if (isAll) return; // 'all' overrides everything
    setPerms(prev => prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]);
  };

  return (
    <div className="au__perm-overlay" onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="au__perm-modal">
        <div className="au__perm-header">
          <div>
            <div className="au__perm-title">Page Permissions</div>
            <div className="au__perm-subtitle">{user.email}</div>
          </div>
          <button className="au__icon-btn" onClick={onClose}><X size={16} /></button>
        </div>

        <div className="au__perm-body">
          {/* Full access toggle */}
          <label className={`au__perm-row au__perm-row--all${isAll ? ' au__perm-row--on' : ''}`}>
            <div className="au__perm-row-left">
              <Shield size={15} color={isAll ? '#7C3AED' : '#9CA3AF'} />
              <span>Full Access (all pages)</span>
            </div>
            <div className={`au__toggle${isAll ? ' au__toggle--on' : ''}`} onClick={() => toggle('all')}>
              <div className="au__toggle-knob" />
            </div>
          </label>

          <div className="au__perm-divider">— or select specific pages —</div>

          {ALL_PAGES.map(({ key, label }) => {
            const on = isAll || perms.includes(key);
            return (
              <label key={key} className={`au__perm-row${on ? ' au__perm-row--on' : ''}`}>
                <span>{label}</span>
                <div
                  className={`au__toggle${on ? ' au__toggle--on' : ''}${isAll ? ' au__toggle--disabled' : ''}`}
                  onClick={() => toggle(key)}
                >
                  <div className="au__toggle-knob" />
                </div>
              </label>
            );
          })}
        </div>

        <div className="au__perm-footer">
          <button className="au__btn au__btn--ghost" onClick={onClose}>Cancel</button>
          <button className="au__btn au__btn--primary" onClick={() => onSave(perms)}>
            <Save size={13} /> Save Permissions
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Reset Password Modal ─────────────────────────────────────────────────────
const ResetPwModal: FC<{
  user: AdminUser;
  token: string;
  onClose: () => void;
}> = ({ user, token, onClose }) => {
  const [pw,      setPw]      = useState('');
  const [showPw,  setShowPw]  = useState(false);
  const [saving,  setSaving]  = useState(false);
  const [done,    setDone]    = useState(false);
  const [err,     setErr]     = useState('');

  const handleSave = async () => {
    if (pw.length < 6) { setErr('Password must be at least 6 characters.'); return; }
    setErr(''); setSaving(true);
    const resp = await dispatch(actions.adminResetPassword, { password: pw }, token, { userId: user._id });
    setSaving(false);
    if (resp?.error) setErr(resp.error.message);
    else setDone(true);
  };

  return (
    <div className="au__perm-overlay" onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="au__perm-modal" style={{ maxWidth: 380 }}>
        <div className="au__perm-header">
          <div>
            <div className="au__perm-title">Reset Password</div>
            <div className="au__perm-subtitle">{user.email}</div>
          </div>
          <button className="au__icon-btn" onClick={onClose}><X size={16} /></button>
        </div>
        <div className="au__perm-body" style={{ gap: '0.75rem' }}>
          {done ? (
            <div style={{ textAlign: 'center', padding: '1rem 0', color: '#1D9E75', fontWeight: 600 }}>
              ✓ Password updated successfully
            </div>
          ) : (
            <>
              {err && (
                <div style={{ background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 8,
                  padding: '0.5rem 0.75rem', fontSize: '0.78rem', color: '#991B1B' }}>
                  {err}
                </div>
              )}
              <div style={{ position: 'relative' }}>
                <input
                  type={showPw ? 'text' : 'password'}
                  value={pw}
                  onChange={e => setPw(e.target.value)}
                  placeholder="New password (min 6 chars)"
                  autoFocus
                  style={{
                    width: '100%', padding: '0.55rem 2.5rem 0.55rem 0.75rem',
                    border: '1.5px solid #E2E4E9', borderRadius: 8,
                    fontSize: '0.85rem', fontFamily: 'inherit', outline: 'none', boxSizing: 'border-box',
                  }}
                />
                <button
                  type="button"
                  onClick={() => setShowPw(s => !s)}
                  style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)',
                    background: 'none', border: 'none', cursor: 'pointer', color: '#9CA3AF' }}
                >
                  {showPw ? <EyeOff size={15} /> : <Eye size={15} />}
                </button>
              </div>
            </>
          )}
        </div>
        <div className="au__perm-footer">
          <button className="au__btn au__btn--ghost" onClick={onClose}>
            {done ? 'Close' : 'Cancel'}
          </button>
          {!done && (
            <button className="au__btn au__btn--primary" disabled={saving} onClick={handleSave}
              style={{ background: '#378ADD' }}>
              <KeyRound size={13} /> {saving ? 'Saving…' : 'Set Password'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// ─── Main Page ────────────────────────────────────────────────────────────────
const AdminUsersPage: FC = () => {
  const { contextStore } = useContext(ContextStore);
  const token = contextStore.token;

  const [users,   setUsers]   = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter,  setFilter]  = useState<'all' | 'pending' | 'approved' | 'denied'>('all');
  const [editUser,  setEditUser]  = useState<AdminUser | null>(null);
  const [resetUser, setResetUser] = useState<AdminUser | null>(null);
  const [saving,    setSaving]    = useState<Record<string, boolean>>({});

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const data = await dispatch(actions.getAdminUsers, {}, token);
      if (Array.isArray(data)) setUsers(data);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const setSavingFor = (id: string, val: boolean) =>
    setSaving(p => ({ ...p, [id]: val }));

  const handleApprove = async (u: AdminUser, perms?: string[]) => {
    setSavingFor(u._id, true);
    const body = perms ? { permissions: perms } : {};
    await dispatch(actions.approveUser, body, token, { userId: u._id });
    await fetchUsers();
    setSavingFor(u._id, false);
  };

  const handleDeny = async (u: AdminUser) => {
    if (!window.confirm(`Deny access for ${u.email}?`)) return;
    setSavingFor(u._id, true);
    await dispatch(actions.denyUser, {}, token, { userId: u._id });
    await fetchUsers();
    setSavingFor(u._id, false);
  };

  const handleSavePerms = async (perms: string[]) => {
    if (!editUser) return;
    setSavingFor(editUser._id, true);
    const isPendingApprove = (editUser as any)._pending_approve;
    if (isPendingApprove) {
      await dispatch(actions.approveUser, { permissions: perms }, token, { userId: editUser._id });
    } else {
      await dispatch(actions.updateUserPermissions, { permissions: perms }, token, { userId: editUser._id });
    }
    setEditUser(null);
    await fetchUsers();
    setSavingFor(editUser._id, false);
  };

  const handleDelete = async (u: AdminUser) => {
    if (!window.confirm(`Delete user ${u.email}? This cannot be undone.`)) return;
    setSavingFor(u._id, true);
    await dispatch(actions.deleteAdminUser, {}, token, { userId: u._id });
    await fetchUsers();
    setSavingFor(u._id, false);
  };

  // ── KPIs ──────────────────────────────────────────────────────────────────
  const nonAdmins  = users.filter(u => u.type !== 'admin');
  const pending    = nonAdmins.filter(u => !u.approved && !u.denied);
  const approved   = nonAdmins.filter(u => u.approved);
  const denied     = nonAdmins.filter(u => u.denied);

  const displayed = users.filter(u => {
    if (u.type === 'admin') return filter === 'all';
    if (filter === 'pending')  return !u.approved && !u.denied;
    if (filter === 'approved') return u.approved;
    if (filter === 'denied')   return u.denied;
    return true;
  });

  const statusLabel = (u: AdminUser) => {
    if (u.type === 'admin')  return { label: 'Admin',    cls: 'au__badge--admin'    };
    if (u.denied)            return { label: 'Denied',   cls: 'au__badge--denied'   };
    if (u.approved)          return { label: 'Approved', cls: 'au__badge--approved' };
    return                          { label: 'Pending',  cls: 'au__badge--pending'  };
  };

  return (
    <div className="au">
      {editUser && (
        <PermEditor
          user={editUser}
          onSave={handleSavePerms}
          onClose={() => setEditUser(null)}
        />
      )}
      {resetUser && (
        <ResetPwModal
          user={resetUser}
          token={token}
          onClose={() => setResetUser(null)}
        />
      )}

      {/* ── Header ── */}
      <div className="au__header">
        <div className="au__header-left">
          <div className="au__header-icon"><Users size={15} color="#fff" /></div>
          <div>
            <div className="au__title">User Management</div>
            <div className="au__subtitle">Approve access requests and control page permissions</div>
          </div>
        </div>
        <button className="au__btn au__btn--ghost" onClick={fetchUsers} disabled={loading}>
          <RefreshCw size={13} className={loading ? 'au__spin' : ''} />
          Refresh
        </button>
      </div>

      <div className="au__body">

        {/* ── KPI strip ── */}
        <div className="au__kpi-row">
          {[
            { label: 'Total Users', value: nonAdmins.length, color: '#378ADD', bg: '#EFF6FF' },
            { label: 'Pending',     value: pending.length,   color: '#EF9F27', bg: '#FFFBEB',
              urgent: pending.length > 0 },
            { label: 'Approved',    value: approved.length,  color: '#1D9E75', bg: '#F0FDF4' },
            { label: 'Denied',      value: denied.length,    color: '#E24B4A', bg: '#FEF2F2' },
          ].map(k => (
            <div key={k.label} className={`au__kpi${k.urgent ? ' au__kpi--urgent' : ''}`}>
              <div className="au__kpi-value" style={{ color: k.color }}>{k.value}</div>
              <div className="au__kpi-label">{k.label}</div>
            </div>
          ))}
        </div>

        {/* ── Filter tabs ── */}
        <div className="au__tabs">
          {(['all', 'pending', 'approved', 'denied'] as const).map(f => (
            <button
              key={f}
              className={`au__tab${filter === f ? ' au__tab--active' : ''}`}
              onClick={() => setFilter(f)}
            >
              {f === 'pending' && pending.length > 0 && (
                <span className="au__tab-badge">{pending.length}</span>
              )}
              {f.charAt(0).toUpperCase() + f.slice(1)}
            </button>
          ))}
        </div>

        {/* ── Table ── */}
        <div className="au__table-wrap">
          {loading ? (
            <div className="au__empty">
              <RefreshCw size={20} className="au__spin" color="#9CA3AF" />
              <span>Loading users…</span>
            </div>
          ) : displayed.length === 0 ? (
            <div className="au__empty">
              <Users size={24} color="#D1D5DB" />
              <span>No users in this category</span>
            </div>
          ) : (
            <table className="au__table">
              <thead>
                <tr>
                  <th>User</th>
                  <th>Status</th>
                  <th>Page Access</th>
                  <th>Joined</th>
                  <th style={{ textAlign: 'center' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {displayed.map(u => {
                  const st  = statusLabel(u);
                  const isSaving = saving[u._id];
                  const permCount = u.permissions?.includes('all')
                    ? 'All pages'
                    : u.permissions?.length
                      ? `${u.permissions.length} page${u.permissions.length !== 1 ? 's' : ''}`
                      : 'No access';

                  return (
                    <tr key={u._id} className="au__row">
                      <td className="au__cell">
                        <div className="au__user-cell">
                          <div className="au__avatar">
                            {u.email.slice(0, 2).toUpperCase()}
                          </div>
                          <span className="au__email">{u.email}</span>
                        </div>
                      </td>

                      <td className="au__cell">
                        <span className={`au__badge ${st.cls}`}>{st.label}</span>
                      </td>

                      <td className="au__cell">
                        {u.type === 'admin' ? (
                          <span className="au__perm-text au__perm-text--admin">Full Admin Access</span>
                        ) : u.approved ? (
                          <button
                            className="au__perm-edit-btn"
                            onClick={() => setEditUser(u)}
                          >
                            {permCount}
                            <ChevronDown size={11} />
                          </button>
                        ) : (
                          <span className="au__perm-text">—</span>
                        )}
                      </td>

                      <td className="au__cell">
                        <span className="au__date">{fmtDate(u.createdAt)}</span>
                      </td>

                      <td className="au__cell" style={{ textAlign: 'center' }}>
                        <div className="au__actions">
                          {/* Reset password — all users including admin */}
                          <button
                            className="au__action-btn au__action-btn--reset"
                            disabled={isSaving}
                            onClick={() => setResetUser(u)}
                            title="Reset password"
                          >
                            <KeyRound size={13} />
                          </button>

                          {u.type !== 'admin' && (
                            <>
                              {!u.approved && !u.denied && (
                                <>
                                  <button
                                    className="au__action-btn au__action-btn--approve"
                                    disabled={isSaving}
                                    onClick={() => setEditUser({ ...u, _pending_approve: true } as any)}
                                    title="Approve & set permissions"
                                  >
                                    <CheckCircle size={13} /> Approve
                                  </button>
                                  <button
                                    className="au__action-btn au__action-btn--deny"
                                    disabled={isSaving}
                                    onClick={() => handleDeny(u)}
                                    title="Deny access"
                                  >
                                    <XCircle size={13} /> Deny
                                  </button>
                                </>
                              )}
                              {u.denied && (
                                <button
                                  className="au__action-btn au__action-btn--approve"
                                  disabled={isSaving}
                                  onClick={() => handleApprove(u)}
                                  title="Re-approve"
                                >
                                  <CheckCircle size={13} /> Re-approve
                                </button>
                              )}
                              {u.approved && (
                                <button
                                  className="au__action-btn au__action-btn--deny"
                                  disabled={isSaving}
                                  onClick={() => handleDeny(u)}
                                  title="Revoke access"
                                >
                                  <XCircle size={13} /> Revoke
                                </button>
                              )}
                              <button
                                className="au__action-btn au__action-btn--delete"
                                disabled={isSaving}
                                onClick={() => handleDelete(u)}
                                title="Delete user"
                              >
                                <Trash2 size={13} />
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* ── Pending approval note ── */}
        {pending.length > 0 && (
          <div className="au__pending-note">
            <Clock size={13} color="#EF9F27" />
            <span>
              <strong>{pending.length} user{pending.length !== 1 ? 's' : ''}</strong> waiting for approval.
              Click <strong>Approve</strong> to set their page permissions and grant access.
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

// Extend AdminUser type locally for approve flow
declare module './AdminUsers.page' {}

export default AdminUsersPage;
